import { RuleGroupTypeIC } from 'react-querybuilder';

export type SourceType =
  'default' | 'publisherPrePingCriteria' | 'offer' | 'offerAction' | 'offerActionLinkout' | 'offerWallAd' | 'sequence';

export type BulkAction = 'all' | 'weekdays' | 'weekend';

export type Option = {
  label: string;
  value: string;
  group?: string;
  /** Shown greyed out and not selectable; `note` says why (ZET-4737). */
  disabled?: boolean;
  note?: string;
};

export interface PreviousRegistrationEntry {
  includeExcludeAction?: string;
  domains?: string[];
  affiliates?: string[];
  previousRegistrationsMin?: number | null;
  previousRegistrationsMax?: number | null;
}

export interface PreviousRegistrationConfiguration {
  andOrLogic: 'AND' | 'OR' | null;
  previousRegistrations: PreviousRegistrationEntry[];
}

export interface affSubIDConfiguration {
  // Legacy / UI-normalised fields
  includeExclude: string | null;
  affiliate: string | null;
  subIds: string | string[];

  // New backend shape support
  subIdList: AffSubIdListEntry[];
  andOrLogic: 'AND' | 'OR' | 'ANY' | '';
}

export interface AffSubIdListEntry {
  subIds: string | string[];
  affiliate: string | null;
  includeExclude: string | null;
}

export interface DomainAffiliateEntry {
  domains?: string[];
  includeExcludeAction?: string;
  affiliates?: string[];
}

export interface domainAffiliates {
  domainAffiliateEntries: DomainAffiliateEntry[];
}

export interface QualityScoreRange {
  name: string;
  minScore: number | null;
  maxScore: number | null;
  cap: number | null;
  failureProbability: number | null;
  validMultipliers: number | null;
  dayFilters?: string[] | null;
}

export interface QualityScoreBypass {
  name: string;
  bypass_affiliate: number[];
  cap: number | null;
  failureProbability: number | null;
}

export interface QualityScoreCriteria {
  qualityScoreRange: QualityScoreRange[];
  bypass: QualityScoreBypass[];
  cap_reset: {
    resetInterval: string;
    resetDayOfWeek: string;
    resetDayOfMonth: string;
    resetDate: string;
  };
  // Legacy support
  minScore?: string;
  maxScore?: string;
}

export interface InternalPrePing {
  prePingSuppressionType?: string;
  prePingSuppressionAmount?: number;
  prePingSuppressionLevel?: string;
  prePingSuppressionAnswer?: string;
  prePingSuppressionMaxLimit?: number | string;
  additionalOffers?: number[];
  additionalOfferActions?: number[];
}

export interface InternalPrePingConfiguration {
  andOrLogic: string;
  internalPrePings: InternalPrePing[];
}

export interface DayRestriction {
  restriction: string;
  restrictionStart: string;
  restrictionEnd: string;
}

export interface DayParting {
  monday?: DayRestriction;
  tuesday?: DayRestriction;
  wednesday?: DayRestriction;
  thursday?: DayRestriction;
  friday?: DayRestriction;
  saturday?: DayRestriction;
  sunday?: DayRestriction;
}

export interface RandomFailureProbability {
  probability?: string;
  condition?: string;
}

export interface IpqsConfiguration {
  shouldProcess: boolean;
  email: string;
  ip: string;
  phone: string;
  fingerprint: string;
}

export interface CapDependency {
  requireAllCapped: boolean;
  offerIds: number[];
  offerActionIds: number[];
}

// Cap Configuration Types - matching backend DTO structure
export enum CapTypes {
  NONE = 'NONE',
  DAILY = 'DAILY',
  MONTHLY = 'MONTHLY',
  LIFETIME = 'LIFETIME',
  DOW_MONDAY = 'DOW_MONDAY',
  DOW_TUESDAY = 'DOW_TUESDAY',
  DOW_WEDNESDAY = 'DOW_WEDNESDAY',
  DOW_THURSDAY = 'DOW_THURSDAY',
  DOW_FRIDAY = 'DOW_FRIDAY',
  DOW_SATURDAY = 'DOW_SATURDAY',
  DOW_SUNDAY = 'DOW_SUNDAY',
}

export enum RestrictionTypes {
  NONE = 'NONE',
  PARTIAL = 'PARTIAL',
}

export interface CapRestriction {
  type: RestrictionTypes;
  starts: string;
  ends: string;
}

export interface CapInstance {
  type: CapTypes;
  amount: number | null;
  restriction: CapRestriction | null;
}

export interface CapConfiguration {
  caps: CapInstance[];
}

export interface CriteriaState {
  tcpaOptInRequired: boolean;
  tcpaLastOptInInterval: boolean;
  briteVerifyPhoneValidated: boolean;
  briteVerifyEmailValidated: boolean;
  phoneBlacklist: boolean;
  gpc: boolean;
  englishBrowserLanguageRequired: boolean;
  minAge: string;
  maxAge: string;
  browserType: string;
  genderValue: string;
  anuraScore: string;
  requiredUserDataIncludeExclude: string;
  requiredUserDataValues: string[];
  jobKeyword: {
    includeExclude: string;
    jobKeywords: string[];
  };
  previousRegistrations: PreviousRegistrationConfiguration | null;
  nullSourceNewUserIncludeExclude: string;
  aibListIncludeExclude: string;
  aibList: string[];
  userIdLastDigitIncludeExclude: string;
  userIdLastDigitValues: string[];
  devicesIncludeExclude: string;
  devices: string[];
  browsersIncludeExclude: string;
  browsers: string[];
  domainsIncludeExclude: string;
  domains: string[];
  operatingSystemIncludeExclude: string;
  operatingSystems: string[];
  statesIncludeExclude: string;
  statesValues: string[];
  zipsIncludeExclude: string;
  zipsValues: string;
  userAttributesIncludeExclude: string;
  userAttributes: RuleGroupTypeIC | null;
  urlParametersIncludeExclude: string;
  urlParameterRules: RuleGroupTypeIC | null;
  userAgentIncludeExclude: string;
  userAgentRules: RuleGroupTypeIC | null;
  affiliateNamesIncludeExclude: string;
  affiliateNames: string[];
  affSubIDConfiguration: affSubIDConfiguration;
  domainAffiliates: domainAffiliates;
  landingPageGroupIncludeExclude: string;
  landingPageGroupIds: string[];
  landingPageIncludeExclude: string;
  landingPageIds: string[];
  qualityScore: QualityScoreCriteria;
  emailDomainsIncludeExclude: string;
  emailDomains: string[];
  question: string;
  questionAnswers: string[];
  internalPrePings: InternalPrePingConfiguration;
  prePingResultSessionCapture: boolean;
  externalPrePingProcessingEnabled: boolean;
  prePingMethod: string;
  prePingHandlerId: any;
  prePingUrl: string;
  prePingBody: string;
  prePingTimeout: number | undefined;
  prePingHeader1: string;
  prePingHeader2: string;
  prePingHeader3: string;
  prePingCustomHttpStatus: string;
  dailyLimit: string;
  dailyLimitAnswers: string[];
  capInterval: string;
  capAmount: number | null;
  currentCapAmount: number;
  dayHourRestrictions: string;
  randomFailureProbabilities: RandomFailureProbability[];
  ipqsConfiguration: IpqsConfiguration;
  capDependency: CapDependency | null;
  capConfiguration: CapConfiguration | null;
}

export interface CriteriaData {
  criteriaConfiguration?: string;
  tags?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  validationErrors?: Record<string, unknown>;
  validationSummary?: string | null;
  isValid?: boolean;
}

export interface CriteriaPanelProps {
  sourceType?: SourceType;
  criteria?: CriteriaData;
  onChange?: (criteria: CriteriaData) => void;
  showAllErrors?: boolean;
  /** Hide the Caps & Restrictions tab. Offer blocks set this: a daily limit on a
   *  block's own criteria is stored but never enforced, because the cap handler
   *  only runs for Question and Banner offers. */
  hideCapsTab?: boolean;
}

export interface CriteriaPaneProps {
  state: CriteriaState;
  setState: React.Dispatch<React.SetStateAction<CriteriaState>>;
  sourceType: SourceType;
  errors?: Record<string, unknown> | undefined;
  markFieldTouched: (fieldPath: string) => void;
}
